import React, { useEffect, useRef, useState, useMemo } from "react";
import { Col, Form, Row, Spinner } from "react-bootstrap";
import { useSelector } from "react-redux";
import _ from "lodash";
import Select from "react-select";
import { checkAccessList, exportToExcel } from "../common/utilities";
import { CustomCombo, Loader } from "../components/customComponents";
import jsPDF from "jspdf";
import "jspdf-autotable";
import w_logo from "../assets/wavicle.png";
import { utilities } from "../components/utilities";
import { employeeTableHeaders } from "./empMgmtConstants";
import EditEmployee from "./EditEmployee";
import ConfirmDelete from "./ConfirmDelete";
import CustomAccordion from "../components/CustomAccordion"; // ✅ Use accordion instead of table

const FLAG_IMAGES = {
  India: require("../assets/india.png"),
  Canada: require("../assets/canada.png"),
  US: require("../assets/united-states.png"),
};

const EmployeeSearch = () => {
  const [region, setRegion] = useState({ label: "All", value: "All" });
  const [competency, setCompetency] = useState({ label: "All", value: 0 });
  const [band, setBand] = useState({ label: "All", value: 0 });
  const [team, setTeam] = useState({ label: "All", value: 0 });
  const [searchBy, setSearchBy] = useState("employee_id");
  const [searchVal, setSearchVal] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState({});
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  const [regionOptions, setRegionOptions] = useState([]);
  const [teamOptions, setTeamOptions] = useState([]);
  const [competencyOptions, setCompetencyOptions] = useState([]);
  const [bandOptions, setBandOptions] = useState([]);
  const [isLoading, setLoading] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  const handleCopy = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 1500); // reset after 1.5s
  };

  const { employeeList, staticData, staticDataLoading } = useSelector(
    (state) => state.EmployeeListReducer
  );

  const employeeTableHeadersList = employeeTableHeaders(
    staticData,
    checkAccessList("crud_employee")
  );

  // Filter options setup
  useEffect(() => {
    setRegionOptions([
      { value: "All", label: "All" },
      { value: "India", label: "India" },
      { value: "Canada", label: "Canada" },
      { value: "US", label: "US" },
    ]);

    if (staticData) {
      setCompetencyOptions([
        { value: 0, label: "All" },
        ...staticData
          .filter((x) => x.type === "competency")
          .map((x) => ({ value: x.id, label: x.description })),
      ]);
      setBandOptions([
        { value: 0, label: "All" },
        ...staticData
          .filter((x) => x.type === "band")
          .map((x) => ({ value: x.id, label: x.description })),
      ]);
      setTeamOptions([
        { value: 0, label: "All" },
        ...staticData
          .filter((x) => x.type === "team")
          .map((x) => ({ value: x.id, label: x.description })),
      ]);
    }
  }, [staticData]);

  // Filter function simplified
  const applyFilter = (
    selectedRegion = { label: "All" },
    selectedCompetency = { label: "All" },
    selectedBand = { label: "All" },
    selectedTeam = { label: "All" }
  ) => {
    setRegion(selectedRegion);
    setCompetency(selectedCompetency);
    setBand(selectedBand);
    setTeam(selectedTeam);

    return employeeList.filter(
      (emp) =>
        (selectedRegion.label === "All" || emp.geography === selectedRegion.label) &&
        (selectedCompetency.label === "All" || emp.competency === selectedCompetency.label) &&
        (selectedBand.label === "All" || emp.band_type === selectedBand.label) &&
        (selectedTeam.label === "All" || emp.team === selectedTeam.label)
    );
  };

  // Search effect
  useEffect(() => {
    let employees = applyFilter(region, competency, band, team);
    let newResults = [];

    if (_.isEmpty(searchVal)) {
      setResult(employees);
      return;
    }

    const lowerVal = searchVal.toLowerCase();

    if (searchBy === "name") {
      newResults = employees.filter((e) => e.employee_name?.toLowerCase().includes(lowerVal));
    } else if (searchBy === "employee_id") {
      newResults = employees.filter((e) => e.employee_id?.toLowerCase().includes(lowerVal));
    } else if (searchBy === "email_id") {
      newResults = employees.filter((e) => e.email_id?.toLowerCase().includes(lowerVal));
    } else if (searchBy === "skill_set") {
      const searchStrings = lowerVal.split(",");
      newResults = employees.filter((emp) => {
        const skills = [
          ...(emp.skill_set?.toLowerCase().split(",") || []),
          ...(emp.sec_skill_set?.toLowerCase().split(",") || []),
        ];
        return searchStrings.every((s) => skills.includes(s));
      });
    }

    setResult(newResults);
  }, [region, competency, band, team, searchBy, searchVal, employeeList]);

  const searchRef = useRef(null);
  const debounceSearch = useMemo(
    () =>
      _.debounce(() => {
        setSearchVal(searchRef.current.value);
      }, 1000),
    []
  );

  const setResult = (results) => {
    setSearchResults(
      results.map((emp) => {
        // ✅ Normalize & format joining_date
        let joiningDate = emp.joining_date;
        if (joiningDate) {
          const d = new Date(joiningDate);
          const day = String(d.getDate()).padStart(2, "0");
          const month = String(d.getMonth() + 1).padStart(2, "0");
          const year = d.getFullYear();
          joiningDate = `${day}-${month}-${year}`; // dd-mm-yyyy
        }

        return {
          ...emp,
          joining_date: joiningDate,
        };
      })
    );
  };

  // Export PDF
  const handlePdf = () => {
    const doc = new jsPDF("l", "pt", "a4");
    const img = new Image();
    img.src = w_logo;

    doc.addImage(img, "png", 15, 5, 100, 30);
    doc.setFont("HelveticaNeue", "normal").setFontSize(13).text("Resource Matrix List", 15, 50);

    // Note: this was based on CustomTable, remove or adjust if not used anymore
    doc.save("ResourceList.pdf");
  };

  // Export Excel
  const handleExcel = () => {
    if (!searchResults.length) {
      utilities.showToast("No Records to export!");
      return;
    }
    const dataList = searchResults.map((emp) => {
      let record = {};
      employeeTableHeadersList.forEach((field) => {
        if (field.accessor === "delete" || field.accessor === "edit") return;
        record[field.title] = emp[field.accessor];
      });
      return record;
    });
    exportToExcel(dataList, "ResourceList");
  };

  // ✅ Accordion Header
  const EmployeeHeader = ({ emp }) => (
    <div className="d-flex w-100" style={{ gap: "2rem" }}>
      <div style={{ minWidth: "200px" }}>
        {FLAG_IMAGES[emp.geography] && (
          <img
            src={FLAG_IMAGES[emp.geography]}
            alt={emp.geography}
            style={{
              width: 24,
              height: 16,
              marginRight: 8,
              verticalAlign: "middle",
            }}
          />
        )}
        <b>{emp.employee_id}</b>
      </div>
      <div style={{ minWidth: "320px" }}>
        {emp.employee_name}
      </div>
      <div style={{ minWidth: "320px" }}>
        {emp.team}
      </div>
      <div style={{ minWidth: "350px" }}>
        {emp.competency} (Band {emp.band_type})
      </div>
      <div style={{ minWidth: "160px" }}>
        Joining: {emp.joining_date || "-"}
      </div>
    </div>
  );


  // ✅ Accordion Body
  const EmployeeBody = ({ emp }) => (
    <div>
      <table className="table table-striped table-bordered table-hover">
        <tbody>
          {/* Employee ID Row */}
          <tr>
            <th>Employee ID</th>
            <td>
              {emp.employee_id}
              <span
                className="ms-2 position-relative"
                style={{ cursor: "pointer", color: "#0d6efd" }}
                onClick={() => handleCopy(emp.employee_id, "id")}
              >
                <i className="fa fa-copy" />
                {copiedField === "id" && (
                  <span
                    className="position-absolute bg-dark text-white px-2 py-1 rounded"
                    style={{
                      top: "-35px",
                      left: "0",
                      fontSize: "0.75rem",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Copied!
                  </span>
                )}
              </span>
            </td>
          </tr>

          {/* Email Row */}
          <tr>
            <th>Email</th>
            <td>
              {emp.email_id}
              <span
                className="ms-2 position-relative"
                style={{ cursor: "pointer", color: "#0d6efd" }}
                onClick={() => handleCopy(emp.email_id, "email")}
              >
                <i className="fa fa-copy" />
                {copiedField === "email" && (
                  <span
                    className="position-absolute bg-dark text-white px-2 py-1 rounded"
                    style={{
                      top: "-35px",
                      left: "0",
                      fontSize: "0.75rem",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Copied!
                  </span>
                )}
              </span>
            </td>
          </tr>

          {/* Rest of rows */}
          <tr><th>Skill Set</th><td>{emp.skill_set}</td></tr>
          <tr><th>Secondary Skill Set</th><td>{emp.sec_skill_set}</td></tr>
          <tr><th>Team</th><td>{emp.team}</td></tr>
          <tr><th>Joining Date</th><td>{emp.joining_date}</td></tr>
        </tbody>
      </table>

      {checkAccessList("crud_employee") && (
        <div className="mt-2">
          <button
            className="btn btn-sm btn-primary me-2"
            onClick={() => {
              setShowEdit(true);
              setSelectedEmp(emp);
            }}
          >
            <i className="fa fa-edit" /> Edit
          </button>
          <button
            className="btn btn-sm btn-danger"
            onClick={() => {
              setShowDelete(true);
              setSelectedEmp(emp);
            }}
          >
            <i className="fa fa-trash" /> Delete
          </button>
        </div>
      )}
    </div>
  );


  return (
    <div className="mt-4">
      {isLoading || staticDataLoading ? (
        <Loader />
      ) : (
        <>
          <ConfirmDelete
            show={showDelete}
            employee={selectedEmp}
            onHide={() => setShowDelete(false)}
          />
          <EditEmployee
            show={showEdit}
            staticData={staticData}
            employee={selectedEmp}
            onHide={() => setShowEdit(false)}
          />

          {/* Filters */}
          <Row className="mb-3">
            <Col md={3}>
              <Form.Label>Search By</Form.Label>
              <Form.Select
                value={searchBy}
                onChange={(e) => setSearchBy(e.target.value)}
              >
                <option value="employee_id">Employee ID</option>
                <option value="name">Name</option>
                <option value="email_id">Email ID</option>
                <option value="skill_set">Skill Set</option>
              </Form.Select>
            </Col>
            <Col md={7}>
              <Form.Label>Search</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter search term"
                ref={searchRef}
                onChange={debounceSearch}
              />
            </Col>
            <Col className="d-flex align-items-end gap-2">
              <button className="btn btn-primary" onClick={handlePdf}>
                Export PDF
              </button>
              <button className="btn btn-success" onClick={handleExcel}>
                Export Excel
              </button>
            </Col>
          </Row>
          
          <Row className="mb-3">
            <Col>
              <Form.Label>Geography</Form.Label>
              <Select
                value={region}
                options={regionOptions}
                onChange={(opt) => applyFilter(opt, competency, band, team)}
              />
            </Col>
            <Col>
              <Form.Label>Team</Form.Label>
              <Select
                value={team}
                options={teamOptions}
                onChange={(opt) => applyFilter(region, competency, band, opt)}
              />
            </Col>
            <Col>
              <Form.Label>Competency</Form.Label>
              <Select
                value={competency}
                options={competencyOptions}
                onChange={(opt) => applyFilter(region, opt, band, team)}
              />
            </Col>
            <Col>
              <Form.Label>Band</Form.Label>
              <Select
                value={band}
                options={bandOptions}
                onChange={(opt) => applyFilter(region, competency, opt, team)}
              />
            </Col>
          </Row>

          {/* ✅ Accordion instead of CustomTable */}
          {searchResults.length === 0 ? (
            <div className="text-muted text-center mt-5">
              No matching employees found.
            </div>
          ) : (
            <CustomAccordion
              items={searchResults.map((emp) => ({
                header: <EmployeeHeader emp={emp} />,
                body: <EmployeeBody emp={emp} />,
              }))}
            />
          )}
        </>
      )}
    </div>
  );
};

export default EmployeeSearch;
